import {
  differenceInDays,
  format,
  formatDistanceToNow,
  isToday,
  isYesterday,
  max,
  min,
  parse,
  parseISO,
} from "date-fns";
import { formatInTimeZone } from "date-fns-tz";

function getMinMaxDate(dates = null) {
  // Check if the dates are null or an empty array
  if (!dates || dates.length === 0) return {}; // Return empty object if null or empty

  // Calculate the min and max dates
  const minDate = min(dates);
  const maxDate = max(dates);

  // Format the dates as YYYY-MM-DD
  const formattedMinDate = format(minDate, "yyyy-MM-dd");
  const formattedMaxDate = format(maxDate, "yyyy-MM-dd");

  return {
    min: formattedMinDate,
    max: formattedMaxDate,
  };
}

const formatUTCDate = (date) => {
  if (!date) return "-";
  // return formatASTDate(date);
  return format(parseISO(date), "dd-MM-yyyy, hh:mm a") || "-";
};

const getItemsPerRow = () => {
  if (typeof window === "undefined") return 4; // default for SSR

  const width = window.innerWidth;

  if (width >= 1400) return 4; // xxl: col-3
  if (width >= 1200) return 3; // xl: col-4
  if (width >= 768) return 2; // md: col-6
  return 1; // mobile
};

const getPerPage = (rows = 3) => {
  return getItemsPerRow() * rows;
};

const CurrencySymbol = ({ color, width = "31", height = "36" }) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={width}
      height={height}
      viewBox="0 0 31 36"
      fill="none"
    >
      <g clip-path="url(#clip0_4692_40844)">
        <path
          d="M19.2933 31.4807C18.7401 32.7198 18.3745 34.0645 18.2344 35.4746L29.9409 32.9607C30.4941 31.7219 30.8595 30.3769 30.9999 28.9668L19.2933 31.4807Z"
          fill={color || "#31353B"}
        />
        <path
          d="M29.9401 25.4294C30.4933 24.1906 30.8589 22.8456 30.999 21.4355L21.88 23.3947V19.6284L29.9398 17.8981C30.493 16.6593 30.8587 15.3143 30.9987 13.9042L21.8797 15.8617V2.31683C20.4824 3.10938 19.2414 4.16435 18.2327 5.40875V16.6451L14.5857 17.4282V0.474609C13.1884 1.26688 11.9474 2.32212 10.9387 3.56652V18.211L2.77847 19.9627C2.22529 21.2015 1.85934 22.5465 1.71898 23.9566L10.9387 21.9773V26.7204L1.05797 28.8414C0.504779 30.0803 0.139113 31.4252 -0.000976562 32.8354L10.3414 30.6151C11.1833 30.4382 11.9069 29.9354 12.3773 29.2434L14.2741 26.4028V26.4022C14.471 26.1083 14.5857 25.754 14.5857 25.3723V21.1943L18.2327 20.4112V27.9439L29.9398 25.4289L29.9401 25.4294Z"
          fill={color || "#31353B"}
        />
      </g>
      <defs>
        <clipPath id="clip0_4692_40844">
          <rect
            width="31"
            height="35"
            fill="white"
            transform="translate(0 0.474609)"
          />
        </clipPath>
      </defs>
    </svg>
  );
};

const formatFileTypes = (types = []) => {
  if (!Array.isArray(types) || types.length === 0) return "";

  const typeMap = {
    "image/png": "PNG",
    "image/jpeg": "JPG",
    "image/jpg": "JPG",
    "image/webp": "WEBP",
    "image/svg+xml": "SVG",
    "video/mp4": "MP4",
  };

  return types
    .map((type) => typeMap[type] || type.split("/")[1]?.toUpperCase())
    .join(", ");
};

const handleStopPropagation = (e) => {
  e.preventDefault();
  e.stopPropagation();
};

const timeStringToDate = (time) => {
  if (!time) return null;
  return parse(time, "hh:mm a", new Date());
};
const timeStringToDateNotificationSchedule = (time) => {
  if (!time) return null;
  return parse(time, "HH:mm", new Date());
};

const dateToTimeString = (date) => {
  if (!date) return "";
  return format(date, "hh:mm a");
};
const dateToTimeStringNotificationSchedule = (date) => {
  if (!date) return "";
  return format(date, "HH:mm");
};

const toMinutes = (time) => {
  if (!time) return null;

  const date = parse(time, "hh:mm a", new Date());
  if (isNaN(date)) return null;

  return date.getHours() * 60 + date.getMinutes();
};

const separateImages = (currentImages) => {
  const apiImages = [];
  const newImages = [];

  currentImages.forEach((item) => {
    if (typeof item === "string") {
      // It's an API image URL
      apiImages.push(item);
    } else if (item instanceof File) {
      // It's a newly uploaded file
      newImages.push(item);
    }
  });

  return { apiImages, newImages };
};

const getTimeAgo = (date) => {
  if (!date) return "-";
  const parsedDate = typeof date === "string" ? new Date(date) : date;
  if (isToday(parsedDate)) {
    return formatDistanceToNow(parsedDate, { addSuffix: true });
  }
  if (isYesterday(parsedDate)) {
    return "Yesterday";
  }
  const daysDiff = differenceInDays(new Date(), parsedDate);
  if (daysDiff < 7) {
    return `${daysDiff} day${daysDiff > 1 ? "s" : ""} ago`;
  }
  // fallback for older dates
  return formatDistanceToNow(parsedDate, { addSuffix: true });
};

const notificationTimeToExecutionTime = (timeStr) => {
  if (!timeStr) return null;

  // Parse "08:44 PM"
  const date = parse(timeStr, "hh:mm a", new Date());

  return {
    time: format(date, "HH:mm"), // "20:44"
  };
};

// Convert 24-hour format ("10:00") to 12-hour format ("10:00 AM")
const executionTimeToNotificationTime = (time24) => {
  if (!time24) return "";

  try {
    const date = parse(time24, "HH:mm", new Date());
    return format(date, "hh:mm a"); // "10:00 AM"
  } catch (error) {
    console.error("Error parsing time:", error);
    return "";
  }
};
const getLocalizedField = (item, language) => {
  if (!item) return "-";

  // Example: field = "name" → will look for name / nameAr
  const arKey = `ar`;
  const enKey = `en`;
  return language === "ar" ? item[arKey] || "-" : item[enKey] || "-";
};
export function CsvIcon() {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z"
        stroke="white"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M14 2V8H20"
        stroke="white"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <text
        x="12"
        y="16"
        fill="black"
        fontSize="6"
        fontWeight="bold"
        textAnchor="middle"
        fontFamily="Arial, sans-serif"
      >
        CSV
      </text>
    </svg>
  );
}
export function ExcelIcon() {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z"
        stroke="white"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M14 2V8H20"
        stroke="white"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <text
        x="12"
        y="14"
        fill="black"
        fontSize="5"
        fontWeight="bold"
        textAnchor="middle"
        fontFamily="Arial, sans-serif"
      >
        EXCEL
      </text>
    </svg>
  );
}
const isDesktop = window.innerWidth >= 992;
const formatLabel = (label, activeFilterType, isFromLeadPage = false) => {
  if (isFromLeadPage && activeFilterType === "") {
    const hour = parseInt(label, 10);
    if (hour > 24) return label;

    if (isNaN(hour)) return label;

    if (hour === 0) return "12 AM";
    if (hour < 12) return `${hour} AM`;
    if (hour === 12) return "12 PM";

    return `${hour - 12} PM`;
  }

  if (activeFilterType === "DAY" && activeFilterType !== "") {
    const hour = parseInt(label, 10);
    if (hour > 24) return label;

    if (isNaN(hour)) return label;

    if (hour === 0) return "12 AM";
    if (hour < 12) return `${hour} AM`;
    if (hour === 12) return "12 PM";

    return `${hour - 12} PM`;
  }

  if (activeFilterType === "MONTH") {
    const date = new Date(label);

    if (isNaN(date.getTime())) return label;

    const month = date.toLocaleString("en-US", {
      month: "short",
    });
    return `${month}-${date.getDate()}`;

    return isDesktop ? `${month}-${date.getDate()}` : `${date.getDate()}`;
  }

  return label;
};
const formatDateDDMMYYYY = (dateString) => {
  if (!dateString) return "";

  const parts = dateString.split("-");

  // Only format if it looks like a full YYYY-MM-DD date
  if (parts.length !== 3) return ""; // return raw label as fallback

  const [year, month, day] = parts;

  if (!year || !month || !day) return dateString;

  return `${day}-${month}-${year}`;
};
const formatLocalDate = (date) => {
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

export {
  getMinMaxDate,
  formatUTCDate,
  getPerPage,
  CurrencySymbol,
  formatFileTypes,
  handleStopPropagation,
  timeStringToDate,
  dateToTimeString,
  toMinutes,
  separateImages,
  getTimeAgo,
  notificationTimeToExecutionTime,
  executionTimeToNotificationTime,
  timeStringToDateNotificationSchedule,
  dateToTimeStringNotificationSchedule,
  getLocalizedField,
  isDesktop,
  formatLabel,
  formatDateDDMMYYYY,
  formatLocalDate
};
